import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { MarkdownComponent } from 'ngx-markdown';
import { SharedModule } from '@shared/shared.module';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdCrossAngleKey, HD_INCARNATION_CROSS_NAME_KEYS } from '../basic-properties/hd-incarnation-cross-names';
import { HdIncarnationCross } from '../basic-properties/hd-chart-properties.types';
import {
  HdChartGlobalSettingsV1,
  loadHdChartGlobalSettings,
  saveHdChartGlobalSettings,
} from '../chart-tabs/hd-chart-global-settings';
import { MANDALA_GATE_SEQUENCE_CCW, MANDALA_QUARTERS, MANDALA_QUARTER_GATE_SPAN } from '../../mandala/mandala.constants';
import { HdIncarnationCrossInfoService } from './hd-incarnation-cross-info.service';
import { HdIncarnationCrossPanelService, hdIncarnationCrossPlacementFrom } from './hd-incarnation-cross-panel.service';

type HdCrossListView = HdChartGlobalSettingsV1['incarnationCrossListView'];
type HdCrossListOrder = HdChartGlobalSettingsV1['incarnationCrossListOrder'];

/** Kolomvolgorde van de lijst: zoals de profielen lopen, rechte hoek → juxtapositie → linker hoek. */
const HD_CROSS_LIST_ANGLES: readonly HdCrossAngleKey[] = ['right', 'juxtaposition', 'left'];

interface CrossCellVm {
  readonly cross: HdIncarnationCross;
  readonly name: string;
  /** Genormaliseerde zoektekst: naam in NL en EN plus de hoek. */
  readonly searchText: string;
}

/** Cel na het zoekfilter: `null` = valt weg (de kolom blijft staan). */
type FilteredCellVm = CrossCellVm | null;

interface FilteredRowVm extends Omit<CrossRowVm, 'cells'> {
  readonly cells: readonly FilteredCellVm[];
}

interface CrossRowVm {
  /** Poort van de bewuste zon. */
  readonly gate: number;
  /** Kort kwartaallabel ("Initiatie"), voor de kwartaalkolom van de poortweergave. */
  readonly quarter: string;
  readonly cells: readonly CrossCellVm[];
}

interface QuarterVm {
  readonly key: string;
  readonly title: string;
  readonly rows: readonly FilteredRowVm[];
}

/** Hoofdletter- en accentongevoelig zoeken ("educatie" vindt ook "Éducatie"). */
function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/** Kruis uit de registry voor deze hoek en bewuste-zonpoort. */
function findRegistryCross(angleKey: HdCrossAngleKey, gate: number): HdIncarnationCross | null {
  const entry = Object.entries(HD_INCARNATION_CROSS_NAME_KEYS[angleKey])
    .find(([key]) => key.split('-')[0] === String(gate));
  if (!entry) {
    return null;
  }
  const [pSun, pEarth, dSun, dEarth] = entry[0].split('-').map(Number);
  return { gates: [pSun, pEarth, dSun, dEarth], angleKey, name: entry[1] };
}

/** Rij na het zoekfilter, of `null` wanneer geen enkel kruis van de rij overblijft. */
function filterRow(row: CrossRowVm, query: string): FilteredRowVm | null {
  if (!query) {
    return row;
  }
  const cells = row.cells.map(cell => (cell.searchText.includes(query) ? cell : null));
  return cells.some(cell => cell != null) ? { ...row, cells } : null;
}

/** Kwartaalsleutel van een poort, uit de mandala-volgorde (16 poorten per kwartaal). */
function quarterKeyOf(gate: number): string {
  const index = MANDALA_GATE_SEQUENCE_CCW.indexOf(gate);
  return MANDALA_QUARTERS[Math.floor(index / MANDALA_QUARTER_GATE_SPAN)]?.key ?? '';
}

/**
 * Gedeeld paneel met de algemene uitleg over incarnatiekruisen (`general.md`)
 * en daaronder de lijst van alle 192 kruisen, per bewuste-zonpoort met de drie
 * hoeken. Twee weergaven: gegroepeerd per kwartaal (in mandala-volgorde of op
 * poortnummer binnen elk kwartaal) of één lijst op poortnummer met het kwartaal
 * als kolom. Beide keuzes zitten in de gebruikersinstellingen. Een klik opent
 * het paneel van dat kruis zonder chart-profiel, dus met alle profielen open.
 * Een zoekfilter (naam in NL of EN, of de hoek) toont enkel de passende
 * kruisen en hoeveel het er zijn. Eén instantie (vaste sleutel), zodat het
 * algemene document één keer laadt.
 */
@Component({
  selector: 'hd-incarnation-cross-general-panel',
  standalone: true,
  imports: [MarkdownComponent, ESegmentedToggleComponent, SharedModule],
  templateUrl: './hd-incarnation-cross-general-panel.component.html',
  styleUrl: './hd-incarnation-cross-general-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdIncarnationCrossGeneralPanelComponent {
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });
  private readonly infoService = inject(HdIncarnationCrossInfoService);
  private readonly panels = inject(HdIncarnationCrossPanelService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly listView = signal<HdCrossListView>(loadHdChartGlobalSettings().incarnationCrossListView);
  protected readonly listOrder = signal<HdCrossListOrder>(loadHdChartGlobalSettings().incarnationCrossListOrder);

  protected readonly title = computed(() => this.text('generalPanelTitle'));
  protected readonly listTitle = computed(() => this.text('listTitle'));
  protected readonly listIntro = computed(() => this.text('listIntro'));
  protected readonly viewLabel = computed(() => this.text('listViewLabel'));
  protected readonly orderLabel = computed(() => this.text('listOrderLabel'));
  protected readonly quarterColumnLabel = computed(() => this.text('listQuarterColumn'));
  protected readonly generalUrl = computed(() => this.infoService.generalMarkdownUrl(this.lang()));
  protected readonly searchPlaceholder = computed(() => this.text('listSearchPlaceholder'));

  /** Zoekterm van het filter (niet bewaard: een zoekopdracht is geen weergavekeuze). */
  protected readonly searchQuery = signal('');
  private readonly normalizedQuery = computed(() => normalizeSearch(this.searchQuery()));

  /** Aantal kruisen dat het filter doorlaat; enkel getoond tijdens het zoeken. */
  protected readonly matchCountText = computed(() => {
    const query = this.normalizedQuery();
    if (!query) {
      return '';
    }
    const count = this.rowsInMandalaOrder()
      .map(row => filterRow(row, query))
      .reduce((total, row) => total + (row?.cells.filter(cell => cell != null).length ?? 0), 0);
    if (count === 0) {
      return this.text('listNoMatches');
    }
    return this.text(count === 1 ? 'listMatchCountOne' : 'listMatchCount').replaceAll('{count}', String(count));
  });

  protected readonly viewOptions = computed<readonly ESegmentedToggleOption<HdCrossListView>[]>(() => [
    { value: 'quarter', label: this.text('listViews.quarter') },
    { value: 'gate', label: this.text('listViews.gate') },
  ]);

  protected readonly orderOptions = computed<readonly ESegmentedToggleOption<HdCrossListOrder>[]>(() => [
    { value: 'mandala', label: this.text('listOrders.mandala') },
    { value: 'gate', label: this.text('listOrders.gate') },
  ]);

  protected readonly angleLabels = computed(() =>
    HD_CROSS_LIST_ANGLES.map(angle => this.text(`listAngles.${angle}`)));

  /** Alle 64 rijen, per poort, in mandala-volgorde. */
  private readonly rowsInMandalaOrder = computed<readonly CrossRowVm[]>(() => {
    const lang = this.lang();
    const angleLabels = this.angleLabels();
    return MANDALA_GATE_SEQUENCE_CCW.map(gate => ({
      gate,
      quarter: this.text(`listQuarters.${quarterKeyOf(gate)}`),
      cells: HD_CROSS_LIST_ANGLES
        .map(angle => findRegistryCross(angle, gate))
        .filter((cross): cross is HdIncarnationCross => cross != null)
        .map(cross => {
          const nameKey = cross.name ?? '';
          return {
            cross,
            name: this.gateInfo.crossNameIn(nameKey, lang),
            searchText: normalizeSearch([
              this.gateInfo.crossNameIn(nameKey, 'nl'),
              this.gateInfo.crossNameIn(nameKey, 'en'),
              angleLabels[HD_CROSS_LIST_ANGLES.indexOf(cross.angleKey)] ?? '',
            ].join(' | ')),
          };
        }),
    }));
  });

  /** Kwartaalweergave: vier groepen, binnen elke groep mandala-volgorde of poortnummer. */
  protected readonly quarters = computed<readonly QuarterVm[]>(() => {
    const lang = this.lang();
    const rows = this.rowsInMandalaOrder();
    const query = this.normalizedQuery();
    const byGate = this.listOrder() === 'gate';
    return MANDALA_QUARTERS.map(quarter => {
      const quarterRows = rows.slice(quarter.sequenceStartIndex, quarter.sequenceStartIndex + MANDALA_QUARTER_GATE_SPAN);
      const sorted = byGate ? [...quarterRows].sort((a, b) => a.gate - b.gate) : quarterRows;
      return {
        key: quarter.key,
        title: this.translateOr(`humanDesign.chart.mandala.quarters.${quarter.key}.title`, lang, quarter.label),
        rows: sorted
          .map(row => filterRow(row, query))
          .filter((row): row is FilteredRowVm => row != null),
      };
    }).filter(quarter => quarter.rows.length > 0);
  });

  /** Poortweergave: één lijst van poort 1 tot 64, het kwartaal als kolom. */
  protected readonly gateRows = computed<readonly FilteredRowVm[]>(() =>
    [...this.rowsInMandalaOrder()]
      .sort((a, b) => a.gate - b.gate)
      .map(row => filterRow(row, this.normalizedQuery()))
      .filter((row): row is FilteredRowVm => row != null));

  constructor() {
    // Het zoekfilter vergelijkt met de namen in beide talen: beide scopes laden.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected setListView(view: HdCrossListView): void {
    this.listView.set(view);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), incarnationCrossListView: view });
  }

  protected setListOrder(order: HdCrossListOrder): void {
    this.listOrder.set(order);
    saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), incarnationCrossListOrder: order });
  }

  /** Klik in de lijst: het paneel van dat kruis, zonder chart dus met alle profielen open. */
  protected openCross(cross: HdIncarnationCross): void {
    this.panels.openCross({ cross, profile: null }, hdIncarnationCrossPlacementFrom(this.panelRef));
  }

  private translateOr(path: string, lang: string, fallback: string): string {
    const translated = this.transloco.translate(path, undefined, lang);
    return translated === path ? fallback : translated;
  }

  private text(key: string): string {
    return this.gateInfo.crossesInfoTextIn(key, this.lang());
  }
}
